import { ILogger } from "@/androidPlugins/ILogger"
import * as DateFns from "date-fns"
import zlFetch from "zl-fetch"

const methods = [
  "get",
  "post",
  "put",
  "patch",
  "delete",
] as const

interface ApiOptions {
  headers?: HeadersInit
  body?: object | string | undefined
  auth?: string
  debug?: boolean
  returnError?: boolean
  customResponseParser?: boolean
  query?: object
  timeoutMs?: number
  version?: 2 | 3
}

interface ApiResponse<T> {
  headers: Headers
  body: T
  status: number
  statusText: string
  response: Response
}

type RequestDataSource =
  | "path"
  | "query"
  | "header"
  | "cookie"
  | "body"
  | "form"
  | "file"

type RequestSourceErrorMap = {
  [location: string]: string
}

type NinjaErrorBody = {
  detail: string
  code?: string
  requestErrors?: Partial<
    Record<RequestDataSource, RequestSourceErrorMap>
  >
  otherErrors?: Array<string>
}

export class ApiError extends Error {
  status: number
  detail?: string
  code?: string
  requestErrors?: Partial<
    Record<RequestDataSource, RequestSourceErrorMap>
  >
  otherErrors?: Array<string>

  constructor(
    response: ApiResponse<unknown>,
    options?: ErrorOptions
  ) {
    super("API error", options)

    this.status = response.status

    if (isNinjaErrorBody(response.body)) {
      this.detail = response.body.detail
      this.code = response.body.code
      this.requestErrors = response.body.requestErrors
      this.otherErrors = response.body.otherErrors
    }

    this.name = "ApiError"
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

export class FailedToFetchError extends Error {
  constructor(options?: ErrorOptions) {
    super("Failed to fetch", options)

    this.name = "FailedToFetchError"
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

export class FetchTimedOutError extends Error {
  constructor(options?: ErrorOptions) {
    super("Fetch request timed out", options)

    this.name = "FetchTimedOutError"
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

type FailedToFetchResponse = {
  error: Error
}

type ApiMethod = <T>(
  url: string,
  options?: ApiOptions
) => Promise<ApiResponse<T>>

type Api = Record<(typeof methods)[number], ApiMethod>

const buildUrlV2 = (path: string) =>
  `${__API_V2_ROOT__}${path}`
const buildUrlV3 = (path: string) =>
  `${__API_V3_ROOT__}${path}`

const api: Partial<Api> = {}

for (const method of methods) {
  api[method] = async (
    path: string,
    { query, timeoutMs, ...options }: ApiOptions = {}
  ) => {
    const { accessToken, clearTokens } = useAuth()
    const { serialNumber } = useDevice()

    const buildUrl =
      options.version === 3 ? buildUrlV3 : buildUrlV2

    const now = new Date()
    const utc = new Date(
      now.getTime() + now.getTimezoneOffset() * 60_000
    )

    const tracingQuery = {
      _t: `${DateFns.format(utc, "yyyyMMdd'T'HHmmssSSS")}-${Math.random().toString(36).slice(2, 5)}`,
      ...(serialNumber.value
        ? { _d: serialNumber.value }
        : {}),
    }

    const url = buildUrl(
      `${path}${buildQueryString(query ? { ...query, ...tracingQuery } : tracingQuery)}`
    )

    const controller = timeoutMs
      ? new AbortController()
      : null
    const timeoutId =
      controller && timeoutMs
        ? setTimeout(
            () => controller.abort("request timed out"),
            timeoutMs
          )
        : null
    try {
      ILogger.logToDetailLog({
        message: `${method.toUpperCase()} REQUEST : ${url}`,
      })

      const response = await zlFetch(url, {
        auth: accessToken.value,
        ...options,
        method: method.toUpperCase(),
        headers: {
          Accept: "application/json",
        },
        ...(controller
          ? { signal: controller.signal }
          : {}),
      })

      ILogger.logToDetailLog({
        message: `${method.toUpperCase()} SUCCESS : ${url}`,
      })

      return response
    } catch (error) {
      ILogger.logToDetailLog({
        message: `${method.toUpperCase()} FAILED : ${url}`,
      })

      if (error === "request timed out") {
        throw new FetchTimedOutError({ cause: error })
      }

      if (isFailedToFetchResponse(error)) {
        throw new FailedToFetchError({ cause: error.error })
      }

      const typedError = error as ApiResponse<unknown>
      if (typedError.status === 401) {
        clearTokens()
      }

      throw new ApiError(typedError)
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }
}

const isNinjaErrorBody = (
  body: unknown
): body is NinjaErrorBody => {
  return (
    typeof body === "object" &&
    body != null &&
    "detail" in body
  )
}

const isFailedToFetchResponse = (
  error: unknown
): error is FailedToFetchResponse => {
  // Wierd type castings required by vue-tsc but not regular typescript. To investigate later.
  return (
    typeof error === "object" &&
    error != null &&
    "error" in error &&
    (error as { error: unknown }).error instanceof Error &&
    (error as { error: Error }).error.message ===
      "Failed to fetch"
  )
}

export default api as Api
